import { useEffect, useState } from 'react'
import { useLang } from '../i18n/LangProvider'
import { useAsistenciaApi } from '../asistencia/api'
import { t, Lang, umbralMinutaje, UMBRAL_COLOR } from './i18n'
import { DashboardMiembro } from './model'
import { PILL_COLOR } from './pillColors'
import styles from './PanelEstadisticasJugador.module.css'
import { EmptyState } from '../styles/EmptyState'

// Feedback del cliente, 2026-10-06: "en este segundo pantallazo se ve fatal, mejor alinear texto y
// número y usar colores como en el detalle de la fila. Por ejemplo Convocados verde como arriba,
// titulares morado y así el resto" — antes era un `<dl>` sin estilos (etiqueta y valor en líneas
// separadas, sin color). Cada fila ahora alinea etiqueta/valor en la misma línea, con el valor
// coloreado igual que su pill correspondiente en la fila de la jornada.
function Stat({ label, value, color }: { label: string; value: number | string; color?: string }) {
  return (
    <div className={styles.statRow}>
      <span className={styles.statLabel}>{label}</span>
      <span className={styles.statValue} style={color ? { color } : undefined}>
        {value}
      </span>
    </div>
  )
}

function PctBar({ label, pct }: { label: string; pct: number | '--' }) {
  const nivel = umbralMinutaje(pct)
  const color = UMBRAL_COLOR[nivel]
  const width = pct === '--' ? 0 : Math.max(0, Math.min(100, pct))
  return (
    <div className={styles.bar}>
      <span>
        {label} <span style={{ color }}>{pct === '--' ? '--' : `${pct}%`}</span>
      </span>
      <div className={styles.barTrack}>
        <div className={styles.barFill} style={{ width: `${width}%`, backgroundColor: color }} />
      </div>
    </div>
  )
}

export function PanelEstadisticasJugador({
  equipoId,
  miembroEquipoId,
  lang: langProp,
  conTarjetas = false,
}: {
  equipoId: string
  miembroEquipoId: string
  lang?: Lang
  // Issue #62: solo F11 lleva tarjetas y ciclo de sanción.
  conTarjetas?: boolean
}) {
  const ctxLang = useLang()
  const lang = langProp ?? ctxLang
  const api = useAsistenciaApi()
  const [datos, setDatos] = useState<DashboardMiembro | null>(null)
  // Bug real, feedback del cliente 2026-10-05: "no puedo abrir la jornada 1 y ver las
  // estadísticas" — este panel (botón 📊 de cada jugador) no capturaba errores de carga en
  // absoluto; si la petición fallaba, `datos` se quedaba en `null` para siempre y el componente
  // devolvía `null` (invisible, sin spinner ni mensaje), indistinguible de "no pasa nada".
  const [error, setError] = useState(false)

  function cargar() {
    setError(false)
    api
      .get(`/equipos/${equipoId}/dashboard`)
      .then((res: DashboardMiembro[]) => {
        setDatos(res.find((m) => m.miembro_equipo_id === miembroEquipoId) ?? null)
      })
      .catch(() => setError(true))
  }

  useEffect(() => {
    cargar()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [equipoId, miembroEquipoId])

  if (error) {
    return (
      <div>
        <p>{t('errorGenerico', lang)}</p>
        <button onClick={cargar}>{t('reintentar', lang)}</button>
      </div>
    )
  }

  if (!datos) return null

  if (datos.jornadasDesdeDebut === 0) {
    return <EmptyState text={t('sinJornadas', lang)} icon="📅" />
  }

  return (
    <div>
      <div className={styles.stats}>
        {/* Issue #61, 2026-10-06: "que cada dato tenga un color diferente para distinguirlo
            rápido" — Conv/Jugados/Titular/bajas ya usaban el color de su pill; el resto
            (Jornadas, Partidos no jugados, Minutos, Goles) se queda cada uno con un color propio,
            distinto de los demás. */}
        <Stat label={t('jornadas', lang)} value={datos.jornadasDesdeDebut} color="var(--color-info)" />
        <Stat label={t('conv2', lang)} value={datos.convocados} color={PILL_COLOR.conv} />
        <Stat label={t('jugados', lang)} value={datos.jugados} color={PILL_COLOR.jug} />
        {/* Issue #47, 2026-10-02: "partidos no jugados" explícito, además de los ya existentes
            (jornadas, convocados, jugados, titular, minutos, %). Disponible (sin baja) pero no
            jugado — p. ej. convocado y no usado, o directamente no convocado. */}
        <Stat label={t('noJugados', lang)} value={datos.disponibles - datos.jugados} color="var(--color-danger)" />
        <Stat label={t('titular', lang)} value={datos.titulares} color={PILL_COLOR.tit} />
        <Stat label={t('minutos', lang)} value={datos.minutos} color="var(--color-warning)" />
        <Stat label={t('goles', lang)} value={datos.goles} color="var(--color-brand)" />
        {conTarjetas && (
          <>
            {/* Ciclo en curso (vuelve a 0 cada 5 amarillas) y nº de sanciones acumuladas. */}
            <Stat label={`🟨 ${t('amarillas', lang)}`} value={`${datos.amarillas_ciclo ?? 0}/5`} color="var(--color-warning)" />
            <Stat label={`🟥 ${t('rojas', lang)}`} value={datos.rojas ?? 0} color="var(--color-danger)" />
            <Stat
              label={t('sanciones', lang)}
              value={datos.sanciones ?? 0}
              color={(datos.sanciones ?? 0) > 0 ? 'var(--color-danger)' : 'var(--color-text-secondary)'}
            />
          </>
        )}
        {Object.entries(datos.bajas)
          .filter(([, count]) => count > 0)
          .map(([tipo, count]) => (
            <Stat key={tipo} label={tipo} value={count} color={PILL_COLOR[tipo as keyof typeof PILL_COLOR]} />
          ))}
      </div>

      <PctBar label={t('porcTotal', lang)} pct={datos.porcentaje_total} />
      <PctBar label={t('porcConv', lang)} pct={datos.porcentaje_conv} />
      <PctBar label={t('porcDisp', lang)} pct={datos.porcentaje_disp} />

      {datos.alerta === 'intervenir' && (
        <span className={`${styles.alerta} ${styles.alertaIntervenir}`}>{t('alertaIntervenir', lang)}</span>
      )}
      {datos.alerta === 'vigilar' && (
        <span className={`${styles.alerta} ${styles.alertaVigilar}`}>{t('alertaVigilar', lang)}</span>
      )}
    </div>
  )
}
